import { Dialog } from '../components/Dialog';
import type { GameStore } from '../../store/GameStore';
import type { GameEvent } from '../../core/events/GameEvents';
import { handleEventChoice } from '../../core/systems/EventSystem';

/**
 * Dialog for random events — shows title, description, and optional choices.
 */
export function openRandomEventDialog(
  parent: HTMLElement,
  store: GameStore,
  event: GameEvent,
  onClose: () => void,
): void {
  if (event.type !== 'RANDOM_EVENT' && event.type !== 'SCRIPTED_EVENT') return;

  const prevSpeed = store.getState().speed;
  store.dispatch({ type: 'SET_SPEED', speed: 0 });

  const hasChoices = event.type === 'RANDOM_EVENT' && event.choices && event.choices.length > 0;

  const dialog = new Dialog({
    title: event.title,
    onClose: () => {
      store.dispatch({ type: 'SET_SPEED', speed: prevSpeed });
      onClose();
    },
    actions: hasChoices ? [] : [{ label: 'OK', style: 'primary', onClick: () => dialog.close() }],
  });

  const content = document.createElement('div');
  content.style.cssText = 'display:flex;flex-direction:column;gap:12px;min-width:300px;max-width:400px;';

  const desc = document.createElement('p');
  desc.textContent = event.description;
  desc.style.cssText = 'font-size:13px;color:var(--color-text-secondary);margin:0;line-height:1.5;';
  content.appendChild(desc);

  const resultEl = document.createElement('div');
  resultEl.style.cssText = 'font-size:12px;color:var(--color-success);min-height:16px;text-align:center;';

  if (hasChoices && event.type === 'RANDOM_EVENT' && event.choices) {
    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;flex-direction:column;gap:6px;';

    for (let i = 0; i < event.choices.length; i++) {
      const choiceLabel = event.choices[i]!;
      const btn = document.createElement('button');
      btn.className = `btn${i === 0 ? ' btn--primary' : ''}`;
      btn.textContent = choiceLabel;
      btn.style.cssText = 'padding:8px 16px;font-size:13px;text-align:left;';
      btn.addEventListener('click', () => {
        const state = store.getState();
        const rng = store.getRng();
        const result = handleEventChoice(event.eventId, i, state as import('../../core/types').SimulationState, rng);
        if (result) {
          resultEl.textContent = result;
          // Disable all choice buttons
          for (const b of btnRow.querySelectorAll('button')) {
            (b as HTMLButtonElement).disabled = true;
          }
          // Add OK button
          const okBtn = document.createElement('button');
          okBtn.className = 'btn btn--primary';
          okBtn.textContent = 'OK';
          okBtn.style.cssText = 'padding:8px 16px;font-size:13px;margin-top:8px;';
          okBtn.addEventListener('click', () => dialog.close());
          content.appendChild(okBtn);
        } else {
          dialog.close();
        }
      });
      btnRow.appendChild(btn);
    }
    content.appendChild(btnRow);
  }

  content.appendChild(resultEl);
  dialog.body.appendChild(content);
  dialog.mount(parent);
}
